import { Component, EventEmitter, inject, OnInit, Output, signal, computed } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { TranslateModule } from '@ngx-translate/core';
import { ConfirmMenuService } from 'src/app/shared/services/confirm-menu/confirm-menu.service';
import { ContentStateService } from '../../content/content-state.service';
import { ContentService } from '../../content/content.service';
import { VersionTileComponent } from 'src/app/shared/components/ui/version-tile/version-tile.component';
import { ContentBuilderVersion } from '../../content/content-generator.service';
import { LoadingButtonComponent } from 'src/app/shared/components/ui/loading-button/loading-button.component';

@Component({
  selector: 'app-questions-analysis-sidebar',
  templateUrl: './questions-analysis-sidebar.component.html',
  styleUrls: ['./questions-analysis-sidebar.component.scss'],
  standalone: true,
  imports: [
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    FormsModule,
    TranslateModule,
    VersionTileComponent,
    LoadingButtonComponent,
  ],
})
export class QuestionsAnalysisSidebarComponent implements OnInit {
  private contentService = inject(ContentService);
  private contentStateService = inject(ContentStateService);
  private confirmMenuService = inject(ConfirmMenuService);

  @Output() close = new EventEmitter<void>();

  searchQuery = signal('');
  loading = signal(false);
  error = signal('');
  reports = signal<any[]>([]);

  currentReport = this.contentStateService.questionsAnalysisResult;

  formattedReports = computed(() => {
    return this.reports().map((report) => ({
      _id: report._id,
      content: '', // Not used in display
      instruction: report.title || report.query,
      createdAt: report.createdAt,
      originalReport: report, // Keep reference if needed
    }));
  });

  quickSuggestions = ['Tendances de la semaine', 'Résumé par sujet', 'Questions sans réponse'];

  constructor() {}

  ngOnInit(): void {
    this.loadReports();
  }

  loadReports() {
    this.contentService.getReports().subscribe({
      next: (res) => {
        this.reports.set(res || []);
      },
      error: (err) => console.error('Error loading reports', err),
    });
  }

  closeSidebar() {
    this.close.emit();
    this.contentStateService.openQuestionsAnalysisSidebar(false);
  }

  generateAnalysis(query?: string) {
    const q = query || this.searchQuery();
    if (!q) return;

    if (query) {
      this.searchQuery.set(query);
    }

    this.loading.set(true);
    this.error.set('');

    this.contentService.generateReport(q).subscribe({
      next: (res) => {
        this.loading.set(false);
        this.contentStateService.setQuestionsAnalysisResult(res);
        this.loadReports(); // Refresh history
      },
      error: (err) => {
        console.error(err);
        this.loading.set(false);
        this.error.set("Une erreur est survenue lors de l'analyse.");
      },
    });
  }

  selectReport(version: any) {
    // version is the mapped object, we might need the original report
    // In this case, since we just mapped it, we can pass it, or if selectReport needs specific fields
    // setQuestionsAnalysisResult usually expects the report object.
    // My formattedReports includes `originalReport`.
    const report = version.originalReport || this.reports().find((r) => r._id === version._id);
    if (report) {
      this.contentStateService.setQuestionsAnalysisResult(report);
    }
  }

  deleteReport(trigger: HTMLElement, version: ContentBuilderVersion) {
    this.confirmMenuService.confirm(trigger, 'Global.AreYouSure').subscribe(() => {
      this.contentService.deleteReport(version._id).subscribe(() => {
        this.loadReports();
      });
    });
  }
}
